import * as R from 'ramda';
import { handleActions } from 'redux-actions';

import * as commonReducers from './common';
import * as actions from '../actions/companies';

import { isNeededResetDataSearch, areArraysEqual } from '../../utils';
import { getUpdatedIds } from './helpers';

const {
  create,
  update,
  fetch,
  remove,
} = commonReducers.operationTypes;


const fetchCompaniesSuccessReducer = (state, {
  payload: {
    ids, companies = {}, existsMore, notNewPage = false, count = 0, polling = false,
  } = {},
}) => ({
  ...state,
  count,
  [fetch]: false,
  ids: getUpdatedIds(state, ids, polling),
  items: { ...state.items, ...companies },
  page: notNewPage ? state.page : state.page + 1,
  existsMore: R.isNil(existsMore) ? state.existsMore : existsMore,
});

const searchCompaniesFailureReducer =
  R.mergeDeepLeft({ [fetch]: false, ids: [], existsMore: false });

const searchCompaniesSuccessReducer = (
  state,
  {
    payload: {
      companies = {}, existsMore, ids, count = 0, notNewPage = false, polling = false,
    } = {},
  },
) => ({
  ...state,
  count,
  [fetch]: false,
  ids: getUpdatedIds(state, ids, polling),
  items: { ...state.items, ...companies },
  page: notNewPage ? state.page : state.page + 1,
  existsMore: R.isNil(existsMore) ? state.existsMore : existsMore,
});

const createCompanyReducer = (
  state,
  { payload },
) => ({
  ...state,
  [create]: false,
  count: state.count + 1,
  ids: R.union(state.ids, [payload._id]),
  items: R.assoc(payload._id, payload, state.items),
});

const addCommentReducer = (
  state,
  { payload: { companyId, commentId } },
) => ({
  ...state,
  items: {
    ...state.items,
    [companyId]: {
      ...state.items[companyId],
      comments: R.union(
        R.pathOr([], ['items', companyId, 'comments'], state),
        [commentId],
      ),
    },
  },
});

const removeCommentReducer = (
  state,
  { payload: { companyId, commentId } },
) => ({
  ...state,
  items: {
    ...state.items,
    [companyId]: {
      ...state.items[companyId],
      comments: R.without(
        [commentId],
        R.pathOr([], ['items', companyId, 'comments'], state),
      ),
    },
  },
});

const updateCompanyReducer = (state, { payload }) => ({
  ...state,
  [update]: false,
  items: {
    ...state.items,
    [payload._id]: {
      ...state.items[payload._id],
      ...payload,
    },
  },
});

const deleteCompanyReducer = (state, { payload }) => ({
  ...state,
  [remove]: false,
  count: state.count - 1,
  ids: R.without([payload], state.ids),
  items: R.dissoc(payload, state.items),
});

const resetSearchDataReducer = (state, { payload: { prevSearchTerm = '', searchTerm = '' } = {} }) => ({
  ...state,
  ...isNeededResetDataSearch(prevSearchTerm, searchTerm)
    ? {
      ids: [],
      page: 0,
      count: 0,
      existsMore: false,
    }
    : {},
  [fetch]: false,
});

const setNewFilter = (state, { payload: { filterType, data } }) => ({
  ...state,
  ids: [],
  page: 0,
  count: 0,
  filters: {
    ...state.filters,
    [filterType]: data,
  },
  existsMore: false,
});

const resetFiltersReducer = state => ({
  ...state,
  ids: [],
  page: 0,
  count: 0,
  filters: {
    countries: [],
  },
  existsMore: false,
});

const resetDataReducer = state => ({
  ...state,
  ids: [],
  page: 0,
  count: 0,
  existsMore: false,
  ...commonReducers.defaultState,
});

const updateCompanyContactsReducer = (state, {
  payload: {
    type,
    contact,
  },
}) => {
  const company = state.items[contact.companyId];
  if (company) {
    // Contact was updated
    if (type === 'contact-update') {
      // get requestsIds that should be removed from the Company
      const requestsToRemove = contact.requests.reduce((acc, { _id, contacts }) => {
        // get other Request's Company's _id (from the Request's Contacts)
        const companiesIds = contacts
          .filter(c => c._id !== contact._id)
          .map(({ companyId }) => companyId)
          .filter(id => id);

        // if the Request doesn't belong to other Company's Contact we need to remove it
        if (!R.contains(company._id, companiesIds)) {
          acc.push(_id);
        }

        return acc;
      }, []);

      return {
        ...state,
        items: {
          ...state.items,
          [contact.companyId]: {
            ...company,
            contacts: R.without([contact._id], company.contacts),
            requests:
              R.without(requestsToRemove, company.requests),
          },
        },
      };
    }

    // Contact was deleted (with Requests)
    if (type === 'contact-delete') {
      return {
        ...state,
        items: {
          ...state.items,
          [contact.companyId]: {
            ...company,
            contacts: R.without([contact._id], company.contacts),
            requests:
              R.without(contact.requests, company.requests),
          },
        },
      };
    }
  }

  return state;
};

const updateCompanyRequestsReducer = (state, {
  payload: {
    type,
    requestId,
    oldRequestContacts,
    newRequestContacts,
    oldRequestContactsIds,
    newRequestContactsIds,
  },
}) => {
  // Request was deleted
  if (type === 'request-delete') {
    let updatedCompanies = {};

    oldRequestContacts.forEach(({ companyId }) => {
      if (companyId) {
        const company = R.pathOr(null, ['items', companyId], state);

        if (company) {
          updatedCompanies = {
            ...updatedCompanies,
            [company._id]: {
              ...company,
              requests: R.without([requestId], company.requests),
            },
          };
        }
      }
    });

    return {
      ...state,
      items: { ...state.items, ...updatedCompanies },
    };
  }

  // Request was updated
  if (type === 'request-update') {
    // if Request's Contacts were changed
    if (!areArraysEqual(oldRequestContactsIds, newRequestContactsIds)) {
      const commonContactsIds = R.intersection(oldRequestContactsIds, newRequestContactsIds);

      let updatedCompanies = {};

      const oldContactsIds =
        R.filter(i => !R.contains(i, commonContactsIds), oldRequestContactsIds);
      const newContactsIds =
        R.filter(i => !R.contains(i, commonContactsIds), newRequestContactsIds);

      oldContactsIds.forEach((id) => {
        const [oldContact] = oldRequestContacts.filter(({ _id }) => id === _id);

        if (oldContact) {
          const company = R.pathOr(null, ['items', oldContact.companyId], state);

          if (company) {
            const otherRequestContacts = R.intersection(commonContactsIds, R.pathOr([], ['contacts'], company));

            if (R.isEmpty(otherRequestContacts)) {
              /*
                remove Request from the Company if Request's Contacts were unassigned from
                the Company and other Request's Contact do not belong to the Company
               */
              updatedCompanies = {
                ...updatedCompanies,
                [company._id]: {
                  ...company,
                  requests: R.without([requestId], company.requests),
                },
              };
            }
          }
        }
      });

      newContactsIds.forEach((id) => {
        const [newContact] = newRequestContacts.filter(({ _id }) => id === _id);

        if (newContact) {
          const company = R.pathOr(null, ['items', newContact.companyId], state);

          if (company) {
            // add Request from Company if new Request's Contact belongs to the Company
            updatedCompanies = {
              ...updatedCompanies,
              [company._id]: {
                ...company,
                requests: R.union([requestId], company.requests),
              },
            };
          }
        }
      });

      return {
        ...state,
        items: { ...state.items, ...updatedCompanies },
      };
    }
  }

  return state;
};

export const companiesReducers = handleActions(
  {
    // create
    [actions.createCompanyRequest]: commonReducers.commonRequestReducer(create),
    [actions.createCompanyFailure]: commonReducers.commonFailureReducer(create),
    [actions.createCompanySuccess]: createCompanyReducer,

    // update
    [actions.updateCompanyRequest]: commonReducers.commonRequestReducer(update),
    [actions.updateCompanyFailure]: commonReducers.commonFailureReducer(update),
    [actions.updateCompanySuccess]: updateCompanyReducer,

    // delete
    [actions.deleteCompanyRequest]: commonReducers.commonRequestReducer(remove),
    [actions.deleteCompanyFailure]: commonReducers.commonFailureReducer(remove),
    [actions.deleteCompanySuccess]: deleteCompanyReducer,

    // fetch
    [actions.fetchCompaniesRequest]: commonReducers.commonRequestReducer(fetch),
    [actions.fetchCompaniesFailure]: commonReducers.commonFailureReducer(fetch),
    [actions.fetchCompaniesSuccess]: fetchCompaniesSuccessReducer,

    // search
    [actions.searchCompaniesRequest]: commonReducers.commonRequestReducer(fetch),
    [actions.searchCompaniesFailure]: searchCompaniesFailureReducer,
    [actions.searchCompaniesSuccess]: searchCompaniesSuccessReducer,

    // Company's Comments
    [actions.addCompanyComment]: addCommentReducer,
    [actions.removeCompanyComment]: removeCommentReducer,

    // Company's Contacts / Requests
    [actions.updateCompanyContacts]: updateCompanyContactsReducer,
    [actions.updateCompanyRequests]: updateCompanyRequestsReducer,

    // reset data
    [actions.resetCompaniesData]: resetDataReducer,
    [actions.resetSearchDataCompanies]: resetSearchDataReducer,

    // filters
    [actions.setFilter]: setNewFilter,
    [actions.resetFilters]: resetFiltersReducer,

    [actions.setPage]: commonReducers.commonSetPageReducer,
  },
  {
    ids: [],
    page: 0,
    count: 0,
    existsMore: true,
    isCommentCreating: false,
    isCommentsFetching: false,
    filters: {
      countries: [],
    },
    ...commonReducers.defaultState,
  },
);



// WEBPACK FOOTER //
// ./src/redux/reducers/companies.js